import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

interface HelpTooltipProps {
  content: string | React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  iconClassName?: string;
  maxWidth?: string;
}

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  side = 'top',
  className = '',
  iconClassName = '',
  maxWidth = '320px',
}) => {
  return (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center transition-colors ${className}`}
            aria-label="Help information"
          >
            <QuestionMarkCircleIcon
              className={`h-4 w-4 text-neutral-400 hover:text-neutral-600 transition-colors ${iconClassName}`}
            />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side={side}
            className="z-50 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
            sideOffset={5}
          >
            <div
              className="px-4 py-3 bg-neutral-900 text-white text-sm rounded-lg shadow-xl border border-neutral-700"
              style={{ maxWidth }}
            >
              {typeof content === 'string' ? (
                <p className="leading-relaxed">{content}</p>
              ) : (
                content
              )}
              <Tooltip.Arrow className="fill-neutral-900" />
            </div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

// Convenience component for section headers with help
interface SectionHeaderWithHelpProps {
  title: string;
  helpText: string | React.ReactNode;
  className?: string;
  titleClassName?: string;
}

export const SectionHeaderWithHelp: React.FC<SectionHeaderWithHelpProps> = ({
  title,
  helpText,
  className = '',
  titleClassName = '',
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <h2 className={`text-xl font-bold text-neutral-900 ${titleClassName}`}>
        {title}
      </h2>
      <HelpTooltip content={helpText} />
    </div>
  );
};

